import { ActionIcon, Box, Flex, Space, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { UserStatus } from '@prisma/client';
import { CheckListId, StammdatenQuestionnaireQuestionKey } from '@web/common';
import { getSexQuestion } from '../../../view/authenticated/participant/stammdaten-questionnaire/stammdaten-questionnaire-specification';
import { MyIcon } from '@web/common-ui';
import { observer } from 'mobx-react';
import { useParticipantDataStore } from '../../../store';
import { InfoItem } from '../../dataCards';
import classes from './ParticipantIdentification.module.css';
import { useParticipantData } from '../hooks/useParticipantData';
import { useTranslation } from 'react-i18next';
import { UpdateParticipantInfoModal } from './UpdateParticipantInfoModal';
import { UpdateAddressModal } from './UpdateAddressModal';
import { NachversandEligibility } from '../NachversandEligibility';
import { DateTime } from 'luxon';

export const PersonalData = observer(() => {
  const participantDataStore = useParticipantDataStore();
  const { userInformation } = useParticipantData();
  const { t } = useTranslation(['stammdaten-questionnaire', 'update-address']);
  const [
    addressModalOpened,
    { open: openAddressModal, close: closeAddressModal },
  ] = useDisclosure(false);
  const { SwissNationality, HeimatOrt, GeburtsOrt } =
    StammdatenQuestionnaireQuestionKey;

  if (!userInformation)
    return (
      <Flex className={classes.noDataBox}>
        <Text ta="center">
          Es sind bisher keine Daten zu diesem Studienteilnehmer vorhanden.
        </Text>
      </Flex>
    );

  const stammdaten =
    userInformation.checkListData?.data?.[CheckListId.StammdatenQuestionnaire]
      ?.questionnaireResult;
  // deactivated (possibly anonymized) participants are shown, but not editable here
  const isDeactivated = userInformation.status === UserStatus.DEACTIVATED;

  const nameAndGender = (() => {
    const name = participantDataStore.getParticipantName();
    const sex = participantDataStore.getParticipantSex();

    const genderText =
      getSexQuestion(t).choices.find((question) => question.value === sex)
        ?.text ?? 'Unbekannt';
    return (
      <Flex gap="10px">
        <Text className={classes.nameText}>{name}</Text>
        <Text className={classes.baseText} fs="italic">
          ({genderText})
        </Text>
      </Flex>
    );
  })();

  const studienCodeAndAHVNummer = (() => {
    if (!stammdaten) return null;

    return (
      <Flex direction="column">
        <Flex gap="5px">
          <Text className={classes.baseText} w="130px">
            Studiencode:
          </Text>
          <Text size="16px" lh="24px" fw="500">
            {userInformation?.userSearchID}
          </Text>
        </Flex>

        <Flex gap="5px" justify="left">
          <Text className={classes.baseText} w="130px">
            AHV-Nummer:
          </Text>
          <Text size="16px" lh="24px" fw="500">
            {stammdaten[StammdatenQuestionnaireQuestionKey.AHVNumber]}
          </Text>
        </Flex>
      </Flex>
    );
  })();

  const heimatOrGeburtsOrt = (() => {
    if (!stammdaten) {
      return null;
    }
    const hasSwissNationality = stammdaten[SwissNationality];
    const label = hasSwissNationality
      ? t('HeimatOrt.Title')
      : t('BirthPlaceID.Title');
    const value = stammdaten[hasSwissNationality ? HeimatOrt : GeburtsOrt];
    if (!value) {
      return null;
    }
    return (
      <InfoItem
        icon={<Box w="24px" />}
        customText={
          <Text className={classes.baseText}>
            {label}: {value}
          </Text>
        }
      />
    );
  })();

  const addressAndBirthdate = (() => {
    const email = participantDataStore.data?.email;
    const phoneNumber = participantDataStore.data?.phoneNumber;
    const plz = stammdaten?.[StammdatenQuestionnaireQuestionKey.PLZ];
    const ort = stammdaten?.[StammdatenQuestionnaireQuestionKey.Ort];

    return (
      <Flex direction="column" c="red" gap="2px">
        {stammdaten && (
          <InfoItem
            align="flex-start"
            icon={
              <MyIcon name="location" style={{ fill: '#4a4a48', height: 24 }} />
            }
            customText={
              <Flex direction="column">
                <Flex gap="8px" align="center">
                  <Text className={classes.baseText}>
                    {stammdaten[StammdatenQuestionnaireQuestionKey.Address] ??
                      'Anonym'}
                  </Text>
                  {!isDeactivated && (
                    <ActionIcon
                      variant="subtle"
                      size="sm"
                      onClick={openAddressModal}
                      aria-label={t('update-address:Title')}
                      title={t('update-address:Title')}
                    >
                      <MyIcon name="edit" style={{ fill: 'grey' }} />
                    </ActionIcon>
                  )}
                </Flex>
                {(plz || ort) && (
                  <Text className={classes.baseText}>
                    {[plz, ort].filter(Boolean).join(' ')}
                  </Text>
                )}
                {userInformation.lastAddressChangeAt && (
                  <Text size="12px" c="dimmed">
                    {t('update-address:LastChangedAt', {
                      date: DateTime.fromJSDate(
                        userInformation.lastAddressChangeAt,
                      ).toFormat('dd.MM.yyyy'),
                    })}
                  </Text>
                )}
              </Flex>
            }
          />
        )}
        {heimatOrGeburtsOrt}
        <NachversandEligibility />
        <InfoItem
          icon={<MyIcon name="mail" style={{ fill: '#4a4a48', height: 24 }} />}
          customText={<Text className={classes.baseText}>{email}</Text>}
        />
        <InfoItem
          icon={<MyIcon name="phone" style={{ fill: '#4a4a48', height: 24 }} />}
          customText={
            <Text lh="28px" className={classes.baseText}>
              {phoneNumber ?? '-'}
            </Text>
          }
        />
        {stammdaten && (
          <InfoItem
            icon={
              <MyIcon name="cake" style={{ fill: '#4a4a48', height: 24 }} />
            }
            customText={
              <Text className={classes.baseText}>
                {stammdaten[StammdatenQuestionnaireQuestionKey.BirthDate]}
              </Text>
            }
          />
        )}
      </Flex>
    );
  })();

  return (
    <Box>
      {nameAndGender}
      <Space h="8px" />
      {studienCodeAndAHVNummer}
      <Space h="16px" />
      {addressAndBirthdate}
      <Space h="16px" />
      <UpdateParticipantInfoModal />
      <UpdateAddressModal
        opened={addressModalOpened}
        onClose={closeAddressModal}
      />
    </Box>
  );
});
